import { useLanguage, t } from "../../../i18n";
import React, { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import ROUTES from "../../../config/routes";
import { Container, Row, Col, Button, Spinner, Card } from "react-bootstrap";
import { HiOutlineHeart, HiOutlineHome, HiOutlinePlus } from "react-icons/hi2";
import Sidebar from "../../../components/layout/Sidebar/Sidebar";
import Header from "../../../components/layout/Header/Header";
import MobileOffcanvasNav from "../../../components/layout/MobileOffcanvasNav/MobileOffcanvasNav";
import StatsCard from "../../../components/layout/StatsCard/StatsCard";
import Footer from "../../../components/layout/Footer/Footer";
import SectionHeader from "../../../components/common/SectionHeader/SectionHeader";
import WhyBaytBuddyCard from "../../../components/common/WhyBaytBuddyCard/WhyBaytBuddyCard";
import HowItWorksCard from "../../../components/common/HowItWorksCard/HowItWorksCard";
import TrustVerifiedSection from "../../../components/common/TrustVerifiedSection/TrustVerifiedSection";
import RecentListingsSection from "../../../components/listing/RecentListingsSection/RecentListingsSection";
import AppComingSoonSection from "../../../components/common/AppComingSoonSection/AppComingSoonSection";
import NafathPromptModal from "../../../components/nafath/NafathPromptModal/NafathPromptModal";
import VerificationNoticeBanner from "../../../components/common/VerificationNoticeBanner/VerificationNoticeBanner";
import { whyBaytBuddyCards, howBaytBuddyWorksSteps } from "../../../data/staticContent";
import { useAuth } from "../../../features/auth/context/AuthContext";
import { useLocation } from "../../../features/location/context/LocationContext";
import useCityListings from "../../../features/listing/hooks/useCityListings";
import useDocumentTitle from "../../../hooks/useDocumentTitle";
import "./HomeDashboard.css";
function normalizeUser(sourceUser = {}) {
  return {
    id: sourceUser?.user_id || sourceUser?.id || "",
    user_id: sourceUser?.user_id || sourceUser?.id || "",
    name: sourceUser?.name || sourceUser?.full_name || "BaytBuddy User",
    full_name: sourceUser?.full_name || sourceUser?.name || "BaytBuddy User",
    email: sourceUser?.email || "",
    mobile: sourceUser?.mobile || "",
    city: sourceUser?.city || sourceUser?.preferred_city || "",
    profile_status: sourceUser?.profile_status || "",
    nafath_status: sourceUser?.nafath_status || ""
  };
}
function toTitleCase(value) {
  return String(value || "").replace(/_/g, " ").replace(/\s+/g, " ").trim().toLowerCase().replace(/\b\w/g, char => char.toUpperCase());
}
function mapGenderPreference(value) {
  const normalized = String(value || "").trim().toLowerCase();
  if (normalized === "male") return "Male Only";
  if (normalized === "female") return "Female Only";
  return value || "Not specified";
}
function mapFurnished(value) {
  if (typeof value === "boolean") {
    return value ? "Yes" : "No";
  }
  const normalized = String(value || "").trim().toLowerCase();
  if (["1", "true", "yes"].includes(normalized)) return "Yes";
  if (["0", "false", "no"].includes(normalized)) return "No";
  return value ? String(value) : "No";
}
function getPrimaryImage(listing = {}) {
  if (listing?.primary_image) {
    return listing.primary_image;
  }
  if (Array.isArray(listing?.images) && listing.images.length > 0) {
    const primaryImage = listing.images.find(item => item?.is_primary) || listing.images[0];
    return primaryImage?.url || "";
  }
  return "";
}
function mapApiListingToCard(listing) {
  return {
    id: listing?.id,
    title: listing?.title || "Untitled Listing",
    image: getPrimaryImage(listing),
    type: toTitleCase(listing?.room_type),
    gender: mapGenderPreference(listing?.gender_preference),
    district: listing?.neighbourhood?.name || listing?.district || listing?.address || "Location not specified",
    city: listing?.city?.name || listing?.city_name || "",
    nationality: Array.isArray(listing?.community_preferences_data) ? listing.community_preferences_data.map(item => item?.name).filter(Boolean) : [],
    price: Number(listing?.rent || 0),
    houseType: toTitleCase(listing?.house_type),
    furnished: mapFurnished(listing?.furnished),
    address: listing?.address || "",
    createdAt: listing?.created_at || "",
    isOwner: Boolean(listing?.is_owner),
    raw: listing
  };
}
export default function HomeDashboard() {
  useLanguage();
  useDocumentTitle("Home");
  const [showMobileNav, setShowMobileNav] = useState(false);
  const [showNafathModal, setShowNafathModal] = useState(false);
  const navigate = useNavigate();
  const {
    user: authUser
  } = useAuth();
  const user = useMemo(() => normalizeUser(authUser || {}), [authUser]);
  const {
    detectedCity,
    detectedCityId
  } = useLocation();
  const isNafathVerified = user?.nafath_status === "verified";
  const resolvedCityName = detectedCity || "Riyadh";
  const resolvedCityId = detectedCityId || "";
  const {listings, isLoading: isRecentListingsLoading} = useCityListings(resolvedCityId, resolvedCityName);
  const recentListings = useMemo(() => listings.map(mapApiListingToCard).sort((a, b) =>
    new Date(b.createdAt || 0).getTime() - new Date(a.createdAt || 0).getTime()
  ).slice(0, 10), [listings]);
  const handlePostRoom = () => {
    if (!isNafathVerified) {
      setShowNafathModal(true);
      return;
    }
    navigate(ROUTES.CREATE_LISTING);
  };
  const handleGoToShortlist = () => {
    navigate(ROUTES.SHORTLIST);
  };
  const handleGoToMyListings = () => {
    navigate(ROUTES.MY_LISTINGS);
  };
  return <div className="bb-dashboard-layout">
      <div className="bb-dashboard-layout__sidebar d-none d-lg-block">
        <Sidebar user={user} />
      </div>

      <div className="bb-dashboard-layout__content">
        <Header onMenuClick={() => setShowMobileNav(true)} />

        <VerificationNoticeBanner user={user} />

        <MobileOffcanvasNav show={showMobileNav} onHide={() => setShowMobileNav(false)} user={user} />


        <NafathPromptModal show={showNafathModal} onHide={() => setShowNafathModal(false)} />


        <main className="bb-dashboard-main">
          <Container fluid>
            <div className="bb-dashboard-welcome">
              <div>
                <h1 className="bb-dashboard-welcome__title bb-primary-text-title">{t("Welcome, ")}
                  {user.name}
                </h1>
                <p className="bb-dashboard-welcome__subtitle">{t("Find your perfect roommate match")}

                </p>
              </div>

              <Button className="bb-dashboard-welcome__cta" onClick={handlePostRoom}>

                <HiOutlinePlus className="me-2" />{t("Post a Room")}

              </Button>
            </div>

            <Row className="g-4">
              <Col md={6}>
                <div onClick={() => navigate(ROUTES.SHORTLIST)} role="button" tabIndex={0}>
                  <StatsCard title={t("My Shortlist")} value="Review saved listings" icon={HiOutlineHeart} />

                </div>
              </Col>

              <Col md={6}>
                <div onClick={() => navigate(ROUTES.MY_LISTINGS)} role="button" tabIndex={0}>
                  <StatsCard title={t("My Listings")} value="Manage posted rooms" icon={HiOutlineHome} />

                </div>
              </Col>
            </Row>

            {isRecentListingsLoading ? <div className="bb-home-recent-listings__loader">
                <Spinner animation="border" size="sm" />
                <span>{t("Loading recent listings...")}</span>
              </div> : recentListings.length ? <RecentListingsSection listings={recentListings} city={resolvedCityName || "All Cities"} /> : null}

            <section className="bb-why-section">
              <SectionHeader title={t("Why BaytBuddy?")} subtitle={t("The smart way to find culturally compatible roommates and affordable housing in Saudi Arabia")} />


              <Row className="g-4">
                {whyBaytBuddyCards.map(item => <Col key={item.id} xl={3} md={6}>
                    <WhyBaytBuddyCard icon={item.icon} title={t(item.title)} description={t(item.description)} />

                  </Col>)}
              </Row>
            </section>

            <section className="bb-how-section">
              <SectionHeader title={t("How BaytBuddy Works?")} subtitle={t("A simple 3-step journey to start finding the right room and roommate experience")} />


              <Row className="g-4">
                {howBaytBuddyWorksSteps.map(item => <Col key={item.id} lg={4}>
                    <HowItWorksCard icon={item.icon} step={item.step} title={t(item.title)} description={t(item.description)} />

                  </Col>)}
              </Row>
            </section>

            <TrustVerifiedSection />
          </Container>
        </main>

        <AppComingSoonSection />

        <Footer />
      </div>
    </div>;
}
